import React, { FC, FocusEvent, PropsWithChildren } from 'react';
import { FRONTEND_DATE_FORMAT } from '@symfa-inc/providence-verizon-types';
import { DatePicker as AntDatePicker } from 'antd';
import moment, { Moment } from 'moment';
import { dateWorker } from '@core/utils/date-worker';
import { readOnlyDatePickerRenderer } from '@core/utils/methods';
import { FormItem } from '@shared/modules';
import { getReadonlyElementFunction } from '../../models/constants';
import { DATE_INPUT_FORMAT_VALIDATOR } from './models/constants';
import { DatePickerProps, IDateInput } from './models/interfaces';

export const DateInput: FC<IDateInput> = ({
  format = FRONTEND_DATE_FORMAT,
  value,
  onChange,
  placeholder = 'Enter date',
  ...props
}: PropsWithChildren<IDateInput>) => (
  <AntDatePicker
    allowClear
    format={format}
    placeholder={placeholder}
    getPopupContainer={(): HTMLElement => document.body}
    value={
      value && value.isValid()
        ? moment(value.format(format), format)
        : undefined
    }
    onChange={(_date: Moment | null, dateString: string): void => {
      onChange?.(dateString ? dateWorker(dateString, format) : null);
    }}
    onBlur={(e: FocusEvent<HTMLInputElement>): void => {
      const typedValue = e.target.value.trim();
      const currentValue = value && value.isValid() ? value.format(format) : '';

      if (typedValue === currentValue) return;

      if (!typedValue) {
        onChange?.(null);

        return;
      }

      const parsed = dateWorker(typedValue, format);

      if (parsed.isValid()) {
        onChange?.(parsed);
      }
    }}
    {...props}
  />
);

export const DatePicker: FC<DatePickerProps> = ({
  id,
  label,
  elementProps,
  format = FRONTEND_DATE_FORMAT,
  formItemProps,
  ...props
}: PropsWithChildren<DatePickerProps>) => (
  <FormItem
    id={id}
    label={label}
    editingElement={<DateInput id={id} format={format} {...elementProps} />}
    readOnlyElement={getReadonlyElementFunction(
      id,
      { formItemProps },
      readOnlyDatePickerRenderer,
    )}
    formItemProps={{
      ...formItemProps,
      rules: [...DATE_INPUT_FORMAT_VALIDATOR, ...(formItemProps?.rules || [])],
    }}
    {...props}
  />
);
